import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CloudArrowUp, FileText, Trash, Download, Key } from "@phosphor-icons/react";

const YEARS = Array.from({ length: 16 }, (_, i) => 2026 - i);

export default function PyqUpload() {
    const [exams, setExams] = useState([]);
    const [examId, setExamId] = useState("kpsc");
    const [stage, setStage] = useState("prelims");
    const [year, setYear] = useState(2024);
    const [title, setTitle] = useState("");
    const [paperNumber, setPaperNumber] = useState("");
    const [paperType, setPaperType] = useState("question");
    const [linkedPaperId, setLinkedPaperId] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [papers, setPapers] = useState([]);
    const navigate = useNavigate();

    const loadPapers = async () => {
        const { data } = await api.get("/pyq-papers", { params: { exam_id: examId, stage } });
        setPapers(data);
    };

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/exams");
            setExams(data);
        })();
    }, []);

    useEffect(() => { loadPapers(); /* eslint-disable-next-line */ }, [examId, stage]);

    // Reset linked paper when year/type changes
    useEffect(() => { setLinkedPaperId(""); }, [year, paperType]);

    const submit = async (e) => {
        e.preventDefault();
        if (!file) return toast.error("Choose a file");
        if (!title.trim()) return toast.error("Add a title");
        if (paperType === "answer_key" && !linkedPaperId) return toast.error("Link the answer key to a question paper");
        setUploading(true);
        const form = new FormData();
        form.append("file", file);
        form.append("exam_id", examId);
        form.append("stage", stage);
        form.append("year", year);
        form.append("title", title);
        form.append("paper_type", paperType);
        if (paperNumber) form.append("paper_number", paperNumber);
        if (linkedPaperId) form.append("linked_paper_id", linkedPaperId);
        try {
            await api.post("/pyq-papers/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(paperType === "answer_key" ? "Answer key uploaded" : "Paper uploaded");
            setFile(null); setTitle(""); setPaperNumber(""); setLinkedPaperId("");
            document.getElementById("pyq-file-input").value = "";
            loadPapers();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (paper_id) => {
        if (!confirm("Delete this file?")) return;
        try {
            await api.delete(`/pyq-papers/${paper_id}`);
            toast.success("Deleted");
            loadPapers();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Question papers for the currently selected year — used to link answer keys
    const linkableQuestions = papers.filter((p) => (p.paper_type || "question") === "question" && p.year === Number(year));

    // Group papers by year for listing
    const grouped = papers.reduce((acc, p) => {
        (acc[p.year] = acc[p.year] || []).push(p);
        return acc;
    }, {});
    const yearsSorted = Object.keys(grouped).sort((a, b) => b - a);

    return (
        <Layout>
            <div className="mb-8 text-center">
                <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
                <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">PYQ Papers · Upload & Manage</h1>
            </div>

            <div className="grid lg:grid-cols-5 gap-6">
                {/* Upload form */}
                <form onSubmit={submit} className="bento-card lg:col-span-2 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <CloudArrowUp size={24} weight="duotone" className="text-[#1E3A8A]" />
                        <div className="font-heading font-bold text-lg">Upload new file</div>
                    </div>

                    {/* Type toggle */}
                    <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-2">File Type</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setPaperType("question")} data-testid="type-question" className={`p-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${paperType === "question" ? "border-[#1E3A8A] bg-[#1E3A8A]/5 text-[#1E3A8A]" : "border-[#BFD3FF] text-slate-600"}`}>
                                <FileText size={16} weight="duotone" className="inline mr-1" /> Question Paper
                            </button>
                            <button type="button" onClick={() => setPaperType("answer_key")} data-testid="type-answer-key" className={`p-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${paperType === "answer_key" ? "border-[#1E3A8A] bg-[#1E3A8A]/5 text-[#1E3A8A]" : "border-[#BFD3FF] text-slate-600"}`}>
                                <Key size={16} weight="duotone" className="inline mr-1" /> Answer Key
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-slate-600">Exam Board</label>
                        <select value={examId} onChange={(e) => setExamId(e.target.value)} data-testid="upload-exam" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]">
                            {exams.map((e) => <option key={e.exam_id} value={e.exam_id}>{e.board} · {e.name}</option>)}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-slate-600">Stage</label>
                            <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="upload-stage" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]">
                                <option value="prelims">Prelims</option>
                                <option value="mains">Mains</option>
                                <option value="interview">Interview</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-600">Year</label>
                            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="upload-year" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]">
                                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Link to question paper (only for answer keys) */}
                    {paperType === "answer_key" && (
                        <div>
                            <label className="text-xs font-semibold text-slate-600">Link to Question Paper *</label>
                            <select value={linkedPaperId} onChange={(e) => setLinkedPaperId(e.target.value)} data-testid="upload-linked" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]">
                                <option value="">— Select question paper for {year} —</option>
                                {linkableQuestions.map((p) => (
                                    <option key={p.paper_id} value={p.paper_id}>{p.title}</option>
                                ))}
                            </select>
                            {linkableQuestions.length === 0 && <div className="text-xs text-amber-700 mt-1">Upload a question paper for {year} first.</div>}
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-semibold text-slate-600">Title</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={paperType === "answer_key" ? "e.g. Paper I · Answer Key" : "e.g. Paper I — General Studies"} data-testid="upload-title" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]" />
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-slate-600">Paper number (optional)</label>
                        <input value={paperNumber} onChange={(e) => setPaperNumber(e.target.value)} placeholder="I, II, III…" data-testid="upload-paperno" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border-2 border-[#BFD3FF] text-sm focus:outline-none focus:border-[#1E3A8A]" />
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-slate-600">File (PDF / DOC / image)</label>
                        <input
                            id="pyq-file-input"
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                            onChange={(e) => setFile(e.target.files[0])}
                            data-testid="upload-file"
                            className="w-full mt-1 text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:font-semibold file:bg-[#1E3A8A] file:text-white hover:file:bg-[#152A66]"
                        />
                        {file && <div className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
                    </div>

                    <button data-testid="upload-btn" type="submit" disabled={uploading} className="btn-primary w-full justify-center disabled:opacity-50">
                        {uploading ? "Uploading…" : `Upload ${paperType === "answer_key" ? "Answer Key" : "Paper"}`}
                    </button>
                </form>

                {/* Listing grouped by year */}
                <div className="lg:col-span-3 space-y-5">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-bold uppercase tracking-widest text-slate-700">
                            {examId.toUpperCase()} · {stage.charAt(0).toUpperCase() + stage.slice(1)} · {papers.length} file{papers.length === 1 ? "" : "s"}
                        </div>
                    </div>

                    {yearsSorted.length === 0 && (
                        <div className="bento-card text-slate-500 text-sm">No files uploaded yet. Use the form on the left to upload.</div>
                    )}

                    {yearsSorted.map((y) => {
                        const questions = grouped[y].filter((p) => (p.paper_type || "question") === "question");
                        const keys = grouped[y].filter((p) => p.paper_type === "answer_key");
                        return (
                            <div key={y} className="bento-card">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="font-mono font-bold text-2xl text-[#1E3A8A]">{y}</div>
                                    <div className="text-xs text-slate-500">{questions.length}Q · {keys.length}K</div>
                                </div>
                                <div className="space-y-3">
                                    {questions.map((p) => {
                                        const linkedKey = keys.find((k) => k.linked_paper_id === p.paper_id);
                                        return (
                                            <div key={p.paper_id} className="space-y-1.5">
                                                <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-[#BFD3FF]">
                                                    <FileText size={22} weight="duotone" className="text-[#1E3A8A] shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-slate-900 truncate">{p.title}</div>
                                                        <div className="text-xs text-slate-500 truncate">{p.original_filename} · {(p.size / 1024).toFixed(1)} KB</div>
                                                    </div>
                                                    <a href={`${backendUrl}/api/pyq-papers/${p.paper_id}/download`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs py-1.5 px-3">
                                                        <Download size={14} /> View
                                                    </a>
                                                    <button onClick={() => remove(p.paper_id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" data-testid={`delete-${p.paper_id}`}>
                                                        <Trash size={18} weight="duotone" />
                                                    </button>
                                                </div>
                                                {linkedKey && (
                                                    <div className="ml-8 flex items-center gap-3 p-2.5 rounded-lg border-2 border-emerald-200 bg-emerald-50/40">
                                                        <Key size={18} weight="duotone" className="text-emerald-700 shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-medium text-emerald-900 truncate">{linkedKey.title}</div>
                                                            <div className="text-[11px] text-emerald-800/60 truncate">Answer key · {linkedKey.original_filename}</div>
                                                        </div>
                                                        <a href={`${backendUrl}/api/pyq-papers/${linkedKey.paper_id}/download`} target="_blank" rel="noopener noreferrer" className="text-xs py-1 px-2.5 rounded-full border-2 border-emerald-600 text-emerald-700 font-semibold hover:bg-emerald-600 hover:text-white">
                                                            <Download size={12} className="inline" /> Key
                                                        </a>
                                                        <button onClick={() => remove(linkedKey.paper_id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50">
                                                            <Trash size={16} weight="duotone" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {/* Orphan answer keys */}
                                    {keys.filter((k) => !questions.find((q) => q.paper_id === k.linked_paper_id)).map((k) => (
                                        <div key={k.paper_id} className="flex items-center gap-3 p-3 rounded-lg border-2 border-amber-200 bg-amber-50/40">
                                            <Key size={22} weight="duotone" className="text-amber-700 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-slate-900 truncate">{k.title}</div>
                                                <div className="text-xs text-amber-700 truncate">Answer key · not linked to a question paper</div>
                                            </div>
                                            <a href={`${backendUrl}/api/pyq-papers/${k.paper_id}/download`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs py-1.5 px-3">
                                                <Download size={14} /> View
                                            </a>
                                            <button onClick={() => remove(k.paper_id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50">
                                                <Trash size={18} weight="duotone" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Layout>
    );
}
